import { FC, useEffect, forwardRef, HTMLAttributes, SyntheticEvent } from 'react';
import { clsx } from 'clsx';
import { CommunicationMessageEvents } from '../interfaces/message-events';
import { DocsLoadingShimmer } from './DocsLoadingShimmer/DocsLoadingShimmer';
import { DocsLoadingError } from './DocsLoadingError/DocsLoadingError';
import { useActiveTheme } from '../../theme/useActiveTheme';
import { getDataIdGenerator } from '../../shared/utils/dataId';

export const DocsIframe = forwardRef<
  HTMLIFrameElement,
  {
    dataId: string;
    iFrameTitle: string;
    isParentLoading?: boolean;
    isIframeLoading: boolean;
    error: boolean;
    docLink: string;
    iframeSourceURL: string;
    className?: string;
    onIframeLoadEvent?: (e: SyntheticEvent<HTMLIFrameElement>) => void;
    onIframeLoadErrorEvent?: VoidFunction;
    sendMessageToIframe: (event: CommunicationMessageEvents, data: object) => void;
    LoadingComponent?: FC;
  } & HTMLAttributes<HTMLDivElement>
>(
  (
    {
      iFrameTitle,
      isParentLoading,
      isIframeLoading,
      error,
      docLink,
      iframeSourceURL,
      className,
      onIframeLoadEvent,
      onIframeLoadErrorEvent,
      sendMessageToIframe,
      LoadingComponent,
      dataId = ''
    },
    ref
  ) => {
    const loadingShimmerVisible = isIframeLoading || !!isParentLoading;
    const iframeClasses = clsx((loadingShimmerVisible || error) && 'd-none');

    const theme = useActiveTheme();

    useEffect(() => {
      if (!isIframeLoading) {
        sendMessageToIframe(CommunicationMessageEvents.DASH_COLOR_MODE_CHANGE, { mode: theme });
      }
    }, [theme, isIframeLoading, isParentLoading]);

    const dataIdGenerator = getDataIdGenerator(dataId, 'docs-iframe');

    return (
      <div className={className} data-id={dataIdGenerator('container')}>
        <iframe
          data-id={dataIdGenerator('iframe')}
          ref={ref}
          title={iFrameTitle}
          src={iframeSourceURL}
          className={iframeClasses}
          name='iframeWindow'
          height='100%'
          width='100%'
          frameBorder={0}
          onLoad={e => {
            if (onIframeLoadEvent) {
              onIframeLoadEvent(e);
            }
          }}
          onError={() => {
            if (onIframeLoadErrorEvent) {
              onIframeLoadErrorEvent();
            }
          }}
        />

        {loadingShimmerVisible ? (
          <>
            {LoadingComponent ? <LoadingComponent /> : null}
            {!LoadingComponent ? <DocsLoadingShimmer dataId={dataIdGenerator('')} /> : null}
          </>
        ) : null}

        {error && !loadingShimmerVisible ? (
          <DocsLoadingError
            title='Setup Guide is temporarily unavailable'
            docLink={docLink}
            dataId={dataIdGenerator('')}
          />
        ) : null}
      </div>
    );
  }
);
